import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Spin } from 'antd';

import { Breadcrumb, ConfirmModal, TitleWithActions } from 'components/components';
import StaticNotification from 'components/components/Notification/StaticNotification';
import VentureBuilderWallet from 'components/VentureBuilderWallet';
import {
  ManagedStartupType,
  useManagedStartups,
  useUpdateVentureBuilder,
  useVentureBuilder,
} from 'packages/venture_builder_repository';
import routes from 'routes';
import showNotification from 'services/utils/showNotification';
import {
  EditVentureBuilderDrawer,
  ManagedStartupsTable,
  ManagedStartupDrawer,
  VentureBuilderInfo,
} from './components';

import './VentureBuilderDetails.scss';

const VentureBuilderDetails = () => {
  const params: { id?: string } = useParams();
  const requestedId = Number(params.id);
  const id = Number.isSafeInteger(requestedId) && requestedId > 0 ? requestedId : 0;

  const { data: ventureBuilder, isLoading, error } = useVentureBuilder(id);
  const {
    data: startups, isFetching: isStartupsLoading, error: startupsError, refetch: refetchStartups,
  } = useManagedStartups(id);
  const updateVentureBuilder = useUpdateVentureBuilder(id);

  const [isStartupOpen, setIsStartupOpen] = useState(false);
  const [editingStartup, setEditingStartup] = useState<ManagedStartupType>();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isStatusConfirmOpen, setIsStatusConfirmOpen] = useState(false);

  if (!id || error) {
    return (
      <div className="content-container">
        <Breadcrumb navigateTo={routes.ventureBuildersUrl} routeName="Venture Builders" />
        <StaticNotification type="warning" message={error?.message || 'Venture Builder not found'} />
      </div>
    );
  }

  if (isLoading || !ventureBuilder) {
    return <div className="content-container"><Spin spinning /></div>;
  }

  const toggleStatus = () => {
    const active = !ventureBuilder.active;
    // Close first: the modal text depends on the status, which changes after the save.
    setIsStatusConfirmOpen(false);
    updateVentureBuilder.mutate({ active }, {
      onSuccess: () => showNotification(active
        ? 'Venture Builder has been activated'
        : 'Venture Builder has been deactivated. Their login is disabled'),
      onError: (mutationError) => showNotification(mutationError.message, true, 400),
    });
  };

  return (
    <div className="content-container">
      <div className="venture-builder-details">
        <Breadcrumb navigateTo={routes.ventureBuildersUrl} routeName="Venture Builders" />

        <TitleWithActions title={ventureBuilder.name || 'Venture Builder'} size="m">
          <Button className="secondary-button" onClick={() => setIsEditOpen(true)}>Edit</Button>
          <Button
            className={ventureBuilder.active ? 'secondary-delete-button' : 'primary-button'}
            disabled={updateVentureBuilder.isPending}
            onClick={() => setIsStatusConfirmOpen(true)}
          >
            {ventureBuilder.active ? 'Deactivate' : 'Activate'}
          </Button>
        </TitleWithActions>

        <VentureBuilderInfo ventureBuilder={ventureBuilder} />

        {!startupsError && !!startups?.length && (
          <div className="venture-builder-details__wallet">
            <VentureBuilderWallet key={id} startups={startups} />
          </div>
        )}

        <TitleWithActions title="Startups" size="s">
          <Button className="primary-button" onClick={() => { setEditingStartup(undefined); setIsStartupOpen(true); }}>
            Add startup
          </Button>
        </TitleWithActions>

        {startupsError ? (
          <StaticNotification
            type="warning"
            message="Unable to load startups and wallet balances"
            primaryButton={{ text: 'Retry', onClick: () => { refetchStartups(); } }}
          />
        ) : (
          <ManagedStartupsTable
            startups={startups || []}
            isLoading={isStartupsLoading}
            onEdit={(startup) => { setEditingStartup(startup); setIsStartupOpen(true); }}
          />
        )}

        <ConfirmModal
          isOpen={isStatusConfirmOpen}
          onCancel={() => setIsStatusConfirmOpen(false)}
          onConfirm={toggleStatus}
          title={ventureBuilder.active ? 'Deactivate Venture Builder' : 'Activate Venture Builder'}
          text={ventureBuilder.active
            ? 'The Venture Builder will no longer be able to log in. Startups, wallets and requests stay unchanged.'
            : 'The Venture Builder will be able to log in again.'}
          okText={ventureBuilder.active ? 'Deactivate' : 'Activate'}
        />

        <ManagedStartupDrawer
          open={isStartupOpen}
          ventureBuilderId={id}
          startup={editingStartup}
          onClose={() => setIsStartupOpen(false)}
        />
        <EditVentureBuilderDrawer
          open={isEditOpen}
          ventureBuilder={ventureBuilder}
          onClose={() => setIsEditOpen(false)}
        />
      </div>
    </div>
  );
};

export default VentureBuilderDetails;
